import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../../context/TeamContext';
import { ChevronDown, Users, Plus, Settings, Check, Loader2 } from 'lucide-react';

const TeamSwitcher = () => {
  const navigate = useNavigate();
  const {
    teams,
    currentTeam,
    teamRole,
    loading,
    switchTeam,
    createTeam,
    canManageTeam,
  } = useTeam();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [switching, setSwitching] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCreating(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitchTeam = async (teamId) => {
    if (teamId === currentTeam?.id) {
      setIsOpen(false);
      return;
    }

    try {
      setSwitching(teamId);
      await switchTeam(teamId);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch team:', error);
    } finally {
      setSwitching(null);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      await createTeam(newTeamName.trim());
      setNewTeamName('');
      setIsCreating(false);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to create team:', error);
    }
  };

  const handleOpenSettings = () => {
    setIsOpen(false);
    navigate('/team-settings');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-700';
      case 'admin':
        return 'bg-blue-100 text-blue-700';
      case 'member':
        return 'bg-green-100 text-green-700';
      case 'viewer':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading teams...</span>
      </div>
    );
  }

  if (!currentTeam) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Users className="w-5 h-5 text-gray-600" />
        <div className="flex flex-col items-start">
          <span className="font-medium text-gray-900 text-sm">{currentTeam.name}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${getRoleBadgeColor(teamRole)}`}>
            {teamRole}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Teams list */}
          <div className="p-2">
            <p className="text-xs text-gray-500 px-2 py-1 font-medium uppercase tracking-wide">
              Your Teams
            </p>
            <div className="max-h-64 overflow-y-auto">
              {teams.map((team) => (
                <button
                  key={team.id}
                  onClick={() => handleSwitchTeam(team.id)}
                  disabled={switching === team.id}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between transition-colors ${
                    team.id === currentTeam.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50'
                  } ${switching === team.id ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    {switching === team.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : team.id === currentTeam.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <div className="w-4 h-4" />
                    )}
                    <span className="font-medium">{team.name}</span>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${getRoleBadgeColor(team.role)}`}>
                    {team.role}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Actions */}
          <div className="p-2">
            {isCreating ? (
              <form onSubmit={handleCreateTeam} className="px-2">
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Team name"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                <div className="flex gap-2 mt-2">
                  <button
                    type="submit"
                    disabled={!newTeamName.trim()}
                    className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreating(false);
                      setNewTeamName('');
                    }}
                    className="flex-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <button
                  onClick={() => setIsCreating(true)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 flex items-center gap-2 text-blue-600"
                >
                  <Plus className="w-4 h-4" />
                  <span className="font-medium">Create New Team</span>
                </button>
                {canManageTeam && (
                  <button
                    onClick={handleOpenSettings}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Team Settings</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSwitcher;
